//shipping management new ui

// components/admin/ShippingManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
  Tooltip,
  Breadcrumbs,
  Link,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Search,
  Refresh,
  Close,
  Star,
  StarBorder,
  LocalShipping,
  AttachMoney,
  LocationOn,
  Warehouse,
  CardGiftcard,
  NavigateNext,
  Home,
  FilterList,
  MoreVert,
  CheckCircle,
  Cancel,
  Visibility,
  ContentCopy,
  Settings,
  Public,
  Money,
  Category,
  Group,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

// Import your API hooks and types
import {
  useListShippingChargesQuery,
  useAddShippingChargeMutation,
  useUpdateShippingChargeMutation,
  useDeleteShippingChargeMutation,
  useSetDefaultShippingChargeMutation,
} from '../../features/admin/shippingApi.ts';

import {
  ShippingCharge,
  ShippingChargeFormData,
  ShippingFormErrors,
  ShippingSnackbarState,
  ShippingChargeType,
} from '../../features/admin/types.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shipping-tabpanel-${index}`}
      aria-labelledby={`shipping-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const getShippingId = (charge: ShippingCharge): number => {
  return Number(charge.model_id || charge.id);
};

const ShippingManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [snackbar, setSnackbar] = useState<ShippingSnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingShipping, setEditingShipping] = useState<ShippingCharge | null>(null);
  const [selectedChargeType, setSelectedChargeType] = useState<string>(ShippingChargeType.FREE);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<ShippingChargeFormData>({
    model_name: '',
    shipping_charge_condition: {},
    active: 1,
    is_default: 0,
    is_restricted: 1,
  });
  const [formErrors, setFormErrors] = useState<ShippingFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // API hooks
  const {
    data: shippingResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListShippingChargesQuery();

  const [addShippingCharge] = useAddShippingChargeMutation();
  const [updateShippingCharge] = useUpdateShippingChargeMutation();
  const [deleteShippingCharge, { isLoading: isDeleting }] = useDeleteShippingChargeMutation();
  const [setDefaultShippingCharge] = useSetDefaultShippingChargeMutation();

  // Handle API response structure
  const shippingCharges = shippingResponse?.result || shippingResponse?.data || [];
  const filteredCharges = shippingCharges.filter((charge: ShippingCharge) => 
    !searchStr || charge.model_name?.toLowerCase().includes(searchStr.toLowerCase())
  );

  const isEditing = Boolean(editingShipping);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
  };

  const resetForm = () => {
    setFormData({
      model_name: '',
      shipping_charge_condition: {},
      active: 1,
      is_default: 0,
      is_restricted: 1,
    });
    setSelectedChargeType(ShippingChargeType.FREE);
    setTabValue(0);
    setFormErrors({});
    setEditingShipping(null);
  };

  const handleAddShipping = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditShipping = (shipping: ShippingCharge) => {
    setFormData({
      model_name: shipping.model_name || '',
      shipping_charge_condition: shipping.shipping_charge_condition || {},
      active: shipping.status ?? shipping.active ?? 1,
      is_default: shipping.is_default ?? (shipping.default ? 1 : 0) ?? 0,
      is_restricted: shipping.is_restricted ?? 1,
    });

    // Determine charge type from condition
    const condition = shipping.shipping_charge_condition;
    if (condition && typeof condition === 'object') {
      if ('charge' in condition && condition.charge === 0) {
        setSelectedChargeType(ShippingChargeType.FREE);
        setTabValue(0);
      } else if ('flat_charge' in condition) {
        setSelectedChargeType(ShippingChargeType.FLAT);
        setTabValue(1);
      }
    }

    setEditingShipping(shipping);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const chargeTypes = [
      ShippingChargeType.FREE,
      ShippingChargeType.FLAT,
      ShippingChargeType.DELIVERY_PARTNER,
    ];
    setSelectedChargeType(chargeTypes[newValue]);

    // Set default condition based on selected type
    const templates = {
      [ShippingChargeType.FREE]: { condition: { charge: 0 }, name: "Free Shipping" },
      [ShippingChargeType.FLAT]: { condition: { flat_charge: 0 }, name: "Flat Rate" },
      [ShippingChargeType.DELIVERY_PARTNER]: { condition: [], name: "Delivery Partners" },
    };
    
    const template = templates[chargeTypes[newValue]];
    setFormData(prev => ({
      ...prev,
      shipping_charge_condition: template.condition,
      model_name: prev.model_name || template.name,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: ShippingFormErrors = {};

    if (!formData.model_name?.trim()) {
      newErrors.model_name = 'Shipping name is required';
    }

    const condition = formData.shipping_charge_condition;

    if (selectedChargeType === ShippingChargeType.FLAT) {
      if (!condition?.flat_charge || condition.flat_charge < 0) {
        newErrors.flat_charge = 'Please enter a valid amount';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsFormSubmitting(true);

    try {
      const baseData = {
        model_name: formData.model_name,
        shipping_charge_condition: formData.shipping_charge_condition,
        active: formData.active ?? 1,
        is_default: formData.is_default ?? 0,
        is_restricted: formData.is_restricted ?? 1,
      };

      let result;
      if (isEditing && editingShipping) {
        const shippingId = getShippingId(editingShipping);
        const requestData = {
          ...baseData,
          model_id: shippingId
        };
        result = await updateShippingCharge(requestData).unwrap();
      } else {
        result = await addShippingCharge(baseData).unwrap();
      }

      setSnackbar({
        open: true,
        message: result.message || `Shipping rate ${isEditing ? 'updated' : 'created'} successfully!`,
        severity: 'success'
      });

      handleFormClose();
      refetch();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error?.data?.message || error.message || `Error ${isEditing ? 'updating' : 'creating'} shipping rate.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (shipping: ShippingCharge) => {
    if (window.confirm('Are you sure you want to delete this shipping rate?')) {
      try {
        const shippingId = getShippingId(shipping);
        setDeletingId(shippingId);
        
        const result = await deleteShippingCharge({ 
          model_id: shippingId 
        }).unwrap();

        setSnackbar({
          open: true,
          message: result.message || 'Shipping rate deleted successfully!',
          severity: 'success'
        });
        
        await refetch();
        
      } catch (err: any) {
        const errorMessage = err?.data?.message || err?.message || 'Error deleting shipping rate.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleSetDefault = async (shipping: ShippingCharge) => {
    try {
      const shippingId = getShippingId(shipping);
      const result = await setDefaultShippingCharge({ 
        model_id: shippingId 
      }).unwrap();

      setSnackbar({
        open: true,
        message: result.message || 'Default shipping rate updated!',
        severity: 'success'
      });
      
      await refetch();
      
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Error setting default shipping rate.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isDeletingCharge = (charge: ShippingCharge): boolean => {
    const chargeId = getShippingId(charge);
    return isDeleting && deletingId === chargeId;
  };

  // Shopify-style status rendering
  const renderStatus = (status: number) => {
    return (
      <Chip 
        label={status === 1 ? "Active" : "Inactive"} 
        color={status === 1 ? "success" : "default"}
        size="small"
        variant="outlined"
        sx={{
          borderRadius: '4px',
          fontWeight: 500,
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      />
    );
  };

  const renderShippingType = (condition: any) => {
    if (!condition) return 'Custom';

    if (typeof condition === 'object') {
      if ('charge' in condition && condition.charge === 0) {
        return 'Free shipping';
      } else if ('flat_charge' in condition) {
        return `Flat rate: $${condition.flat_charge}`;
      }
    }
    
    return 'Custom rate';
  };

  const getShippingIcon = (condition: any) => {
    if (!condition) return <Truck />;

    if (typeof condition === 'object') {
      if ('charge' in condition && condition.charge === 0) {
        return <CardGiftcard />;
      } else if ('flat_charge' in condition) {
        return <Money />;
      }
    }

    return <Truck />;
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view shipping rates.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No shipping rates found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load shipping rates. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  // Shopify-style form content
  const renderFormContent = () => {
    const condition = formData.shipping_charge_condition || {};

    switch (selectedChargeType) {
      case ShippingChargeType.FREE:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CardGiftcard sx={{ fontSize: 48, color: shopifyColors.success, mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ color: shopifyColors.textPrimary }}>
              Free shipping
            </Typography>
            <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
              Customers won't be charged for shipping
            </Typography>
          </Box>
        );

      case ShippingChargeType.FLAT:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ color: shopifyColors.textPrimary, fontWeight: 600 }}>
              Flat rate
            </Typography>
            <TextField
              label="Price"
              type="number"
              value={condition.flat_charge || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                shipping_charge_condition: { flat_charge: Number(e.target.value) }
              }))}
              error={!!formErrors.flat_charge}
              helperText={formErrors.flat_charge}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                }
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Shopify-style Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Settings
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Shipping
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Shipping rates
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage how much you charge for shipping at checkout
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddShipping}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Create shipping rate
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2 }}>
            {/* Search and Filters */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search shipping rates..."
                    value={searchStr}
                    onChange={handleSearch}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 300 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    More filters
                  </Button>
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {searchStr ? (
                  `Showing ${filteredCharges.length} of ${shippingCharges.length} shipping rates`
                ) : (
                  `${shippingCharges.length} shipping rates`
                )}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading shipping rates...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredCharges.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <Truck sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No shipping rates found' : 'No shipping rates yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No shipping rates match "${searchStr}"`
                    : 'Create your first shipping rate to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddShipping}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Create shipping rate
                  </Button>
                )}
              </Card>
            )}

            {/* Shipping Rates List */}
            {!isLoading && filteredCharges.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredCharges.map((charge: ShippingCharge, index: number) => {
                    const chargeId = getShippingId(charge);
                    const isThisDeleting = isDeletingCharge(charge);
                    const isDefault = charge.is_default || charge.default;
                    
                    return (
                      <ListItem
                        key={chargeId}
                        sx={{
                          borderBottom: index < filteredCharges.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                          '&:hover': {
                            backgroundColor: `${shopifyColors.background}80`,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              bgcolor: isDefault ? shopifyColors.warning : shopifyColors.primary,
                              width: 40,
                              height: 40,
                            }}
                          >
                            {getShippingIcon(charge.shipping_charge_condition)}
                          </Avatar>
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                                {charge.model_name}
                              </Typography>
                              {isDefault && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  sx={{
                                    backgroundColor: shopifyColors.warning,
                                    color: '#000',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                              {renderStatus(charge.status ?? charge.active ?? 0)}
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                              {renderShippingType(charge.shipping_charge_condition)}
                            </Typography>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {!isDefault && (
                              <Tooltip title="Set as default">
                                <IconButton
                                  onClick={() => handleSetDefault(charge)}
                                  size="small"
                                  disabled={isThisDeleting}
                                >
                                  <StarBorder />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleEditShipping(charge)}
                                size="small"
                                disabled={isThisDeleting}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDelete(charge)}
                                disabled={isThisDeleting || isDeleting}
                                size="small"
                              >
                                {isThisDeleting ? <CircularProgress size={20} /> : <Delete />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Shopify-style Create/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit shipping rate' : 'Create shipping rate'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormSubmitting}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 1 
                }}>
                  Basic information
                </Typography>
                
                <TextField
                  name="model_name"
                  label="Shipping rate name"
                  value={formData.model_name}
                  onChange={handleInputChange}
                  error={!!formErrors.model_name}
                  helperText={formErrors.model_name}
                  fullWidth
                  required
                  placeholder="e.g., Free shipping, Standard delivery"
                  disabled={isFormSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Shipping Type  Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Shipping type
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: tabValue === 0 ? `2px solid ${shopifyColors.primary}` : `1px solid ${shopifyColors.border}`,
                      borderRadius: '6px',
                      backgroundColor: tabValue === 0 ? `${shopifyColors.primary}08` : 'transparent',
                    }}
                    onClick={() => handleTabChange({} as React.SyntheticEvent, 0)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CardGiftcard sx={{ color: shopifyColors.success }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Free shipping
                        </Typography>
                        <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                          Customers won't be charged for shipping
                        </Typography>
                      </Box>
                    </Box>
                  </Card>

                  <Card 
                    variant="outlined"
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: tabValue === 1 ? `2px solid ${shopifyColors.primary}` : `1px solid ${shopifyColors.border}`,
                      borderRadius: '6px',
                      backgroundColor: tabValue === 1 ? `${shopifyColors.primary}08` : 'transparent',
                    }}
                    onClick={() => handleTabChange({} as React.SyntheticEvent, 1)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Money sx={{ color: shopifyColors.primary }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Flat rate
                        </Typography>
                        <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                          Charge a fixed price for shipping
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>

              {/* Configuration Sections */}
              <Box>
                {renderFormContent()}
              </Box>

              {/* Settings */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Settings
                </Typography>
                  
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          active: e.target.checked ? 1 : 0 
                        }))}
                        color="success"
                      />
                    }
                    label="Active"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          is_default: e.target.checked ? 1 : 0 
                        }))}
                        color="warning"
                      />
                    }
                    label="Set as default shipping rate"
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormSubmitting}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormSubmitting || !formData.model_name?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Create shipping rate'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );  
};

export default ShippingManagement;